import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from "react";
import { getAllData } from "Services/MicrobiologyResultData";
import RequisitionType from "Services/Requisition/RequisitionTypeService";
import { NormalizedData } from "../rowDataUtils";

export interface FilterDataI {
  tabId: number;
  pageSize: number;
  pageNumber: number;
  sortColumn: string;
  sortDirection: string;
  filters: any[];
}

export interface DataI {
  gridHeaders: any[];
  gridColumns: any[];
  gridData: any[];
}

export interface SelectedBoxI {
  requisitionId: any[];
}

type Context = {
  data: DataI;
  setData: React.Dispatch<React.SetStateAction<DataI>>;
  filterData: FilterDataI;
  setFilterData: React.Dispatch<React.SetStateAction<FilterDataI>>;
  searchValue: any;
  setSearchValue: React.Dispatch<React.SetStateAction<any>>;
  loadGridData: (shouldPageLoad?: boolean) => Promise<any>;
  selectedBox: SelectedBoxI;
  setSelectedBox: React.Dispatch<React.SetStateAction<SelectedBoxI>>;
  loading: boolean;
  apiCalls: () => Promise<void>;
  value: number;
  setValue: React.Dispatch<React.SetStateAction<number>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setCheckedAll: React.Dispatch<React.SetStateAction<boolean>>;
  checkedAll: boolean;
  total: number;
  intialValue: FilterDataI;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  rowsToExpand: number[];
  setRowsToExpand: Dispatch<SetStateAction<number[]>>;
  isMasterExpandTriggered: any;
  setIsMasterExpandTriggered: any;
};

const context = createContext<Context>({} as Context);

export const intialValue: FilterDataI = {
  tabId: 1,
  pageSize: 50,
  pageNumber: 1,
  sortColumn: "",
  sortDirection: "",
  filters: [],
};

export default function MicrobiologyResultDataContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [rowsToExpand, setRowsToExpand] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [value, setValue] = useState<number>(0);
  const [isMasterExpandTriggered, setIsMasterExpandTriggered] = useState(false);
  const [filterData, setFilterData] = useState<FilterDataI>(intialValue);
  const [searchValue, setSearchValue] = useState<any>({});
  const [total, setTotal] = useState<number>(0);
  const isFirstLoad = useRef(true);
  const [data, setData] = useState<DataI>({
    gridHeaders: [],
    gridColumns: [],
    gridData: [],
  });
  const [selectedBox, setSelectedBox] = useState<SelectedBoxI>({
    requisitionId: [],
  });

  const getTabs = async (): Promise<any> => {
    try {
      const response = await RequisitionType.viewRequisitionTabs();
      if (response?.data?.data) {
        const tabsData = Array.isArray(response.data.data) ? response.data.data : [];
        const responseTabs = [...tabsData].sort(
          (a: any, b: any) => (a?.sortOrder || 0) - (b?.sortOrder || 0)
        );
        setData((prevVal) => ({
          ...prevVal,
          gridHeaders: responseTabs,
        }));
        if (isFirstLoad.current && responseTabs.length > 0) {
          const firstTab = responseTabs[0];
          setValue(0);
          setFilterData((prev) => ({
            ...prev,
            tabId: firstTab?.tabID ?? prev.tabId,
          }));
          isFirstLoad.current = false;
        }
      } else {
        setData((prevVal) => ({
          ...prevVal,
          gridHeaders: [],
        }));
      }
    } catch (error) {
      console.error("Error loading tabs:", error);
      setData((prevVal) => ({
        ...prevVal,
        gridHeaders: [],
      }));
      return error;
    }
  };

  const loadGridData = async (shouldPageLoad = true): Promise<any> => {
    setLoading(shouldPageLoad);

    const cleanedFiltersData = (filterData?.filters || []).map((item: any) => ({
      ...item,
      filterValue:
        typeof item.filterValue === "string"
          ? item.filterValue.trim()
          : item.filterValue,
    }));

    const searchParams = {
      tabId: filterData?.tabId || 1,
      pageNumber: filterData?.pageNumber || 1,
      pageSize: filterData?.pageSize || 50,
      sortColumn: filterData?.sortColumn || "",
      sortDirection: filterData?.sortDirection || "",
      filters: cleanedFiltersData,
      selectedRow: [0],
    };

    try {
      const response = await getAllData(searchParams);
      if (response?.data) {
        const gridData = Array.isArray(response.data.data) ? response.data.data : [];
        const totalCount =
          response.data.total ?? NormalizedData(gridData[0], "totalcount") ?? 0;
        setData((prevVal) => ({
          ...prevVal,
          gridData: gridData,
        }));
        setTotal(totalCount);
        return response;
      }
    } catch (error) {
      console.error("Error loading grid data:", error);
      setData((prevVal) => ({
        ...prevVal,
        gridData: [],
      }));
      setTotal(0);
      return error;
    } finally {
      setLoading(false);
    }
  };

  const apiCalls = async (): Promise<void> => {
    await getTabs();
    await loadGridData();
  };

  return (
    <context.Provider
      value={{
        data,
        setData,
        filterData,
        total,
        setFilterData,
        searchValue,
        setSearchValue,
        loadGridData,
        selectedBox,
        setSelectedBox,
        loading,
        apiCalls,
        value,
        setValue,
        open,
        setOpen,
        setCheckedAll,
        checkedAll,
        intialValue,
        setLoading,
        rowsToExpand,
        setRowsToExpand,
        isMasterExpandTriggered,
        setIsMasterExpandTriggered,
      }}
    >
      {children}
    </context.Provider>
  );
}

export const useMicrobiologyResultDataContext = () => useContext(context);
